import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Calendar, Trophy, BarChart3, Youtube, FileText, Settings, Vote, MessageCircle, X } from 'lucide-react';
import HighlightCarousel from '../components/HighlightCarousel';
import PWAInstallBanner from '../components/PWAInstallBanner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const WHATSAPP_GROUP_URL = 'https://chat.whatsapp.com/FclBi3J8x1XBFRL07HvftK';

// (no helpers needed)

const HomePage = () => {
  const [votingStatus, setVotingStatus] = useState({ is_open: false, message: '' });
  const [leaderboards, setLeaderboards] = useState({ top_ranking: [], top_scorers: [], top_assists: [] });
  const [highlights, setHighlights] = useState([]);
  const [nextMatch, setNextMatch] = useState(null);
  const [teamPhotoUrl, setTeamPhotoUrl] = useState('');
  const [showWaBanner, setShowWaBanner] = useState(() => {
    try { return localStorage.getItem('domingao_wa_banner_dismissed') !== '1'; } catch { return true; }
  });

  const dismissWaBanner = () => {
    setShowWaBanner(false);
    try { localStorage.setItem('domingao_wa_banner_dismissed', '1'); } catch { /* noop */ }
  };

  useEffect(() => {
    fetchVotingStatus();
    fetchLeaderboards();
    fetchHighlights();
    fetchNextMatch();
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setTeamPhotoUrl(response.data.team_photo_url || '');
    } catch (error) {
      console.error('Erro ao carregar configurações:', error);
    }
  };

  const fetchHighlights = async () => {
    try {
      const response = await axios.get(`${API}/highlights`);
      setHighlights(response.data.highlights || []);
    } catch (error) {
      console.error('Erro ao carregar destaques:', error);
    }
  };

  const fetchNextMatch = async () => {
    try {
      const response = await axios.get(`${API}/matches`);
      const matches = response.data || [];
      if (matches.length > 0) {
        const sorted = matches.sort((a, b) => new Date(a.date) - new Date(b.date));
        setNextMatch(sorted[0]);
      }
    } catch (error) {
      console.error('Erro ao carregar próxima partida:', error);
    }
  };

  const fetchVotingStatus = async () => {
    try {
      const response = await axios.get(`${API}/voting-status`);
      setVotingStatus(response.data);
    } catch (error) {
      console.error('Erro ao verificar status de votação:', error);
    }
  };

  const fetchLeaderboards = async () => {
    try {
      const response = await axios.get(`${API}/leaderboards`);
      setLeaderboards(response.data);
    } catch (error) {
      console.error('Erro ao carregar líderes:', error);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50">
      {/* Header */}
      <header className="bg-zinc-950 text-white py-4 px-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between gap-3">
          <h1 className="text-2xl sm:text-3xl font-black tracking-tighter">
            ⚽ DOMINGÃO
          </h1>
          <div className="flex items-center gap-2">
            <a href="/admin" className="text-sm font-bold hover:text-green-400 transition-colors" data-testid="go-to-admin">
              <Settings size={24} />
            </a>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="bg-white border-b-2 border-zinc-200 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 py-3">
          <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
            <a href="/" className="flex-shrink-0 bg-green-500 text-white px-4 py-2 rounded-lg font-bold text-sm" data-testid="nav-home">
              HOME
            </a>
            <a href="/votar" className="flex-shrink-0 bg-zinc-100 hover:bg-zinc-200 px-4 py-2 rounded-lg font-bold text-sm transition-colors" data-testid="nav-vote">
              <Vote className="inline mr-1" size={16} /> VOTAR
            </a>
            <a href="/estatisticas" className="flex-shrink-0 bg-zinc-100 hover:bg-zinc-200 px-4 py-2 rounded-lg font-bold text-sm transition-colors" data-testid="nav-stats">
              <BarChart3 className="inline mr-1" size={16} /> ESTATÍSTICAS
            </a>
            <a href="/torneios" className="flex-shrink-0 bg-zinc-100 hover:bg-zinc-200 px-4 py-2 rounded-lg font-bold text-sm transition-colors" data-testid="nav-torneios">
              <Trophy className="inline mr-1" size={16} /> TORNEIOS
            </a>
            <a href="/partidas" className="flex-shrink-0 bg-zinc-100 hover:bg-zinc-200 px-4 py-2 rounded-lg font-bold text-sm transition-colors" data-testid="nav-matches">
              <Calendar className="inline mr-1" size={16} /> PARTIDAS
            </a>
            <a href="/rankings" className="flex-shrink-0 bg-zinc-100 hover:bg-zinc-200 px-4 py-2 rounded-lg font-bold text-sm transition-colors" data-testid="nav-rankings">
              <Trophy className="inline mr-1" size={16} /> RANKINGS
            </a>
            <a href="/replays" className="flex-shrink-0 bg-zinc-100 hover:bg-zinc-200 px-4 py-2 rounded-lg font-bold text-sm transition-colors" data-testid="nav-replays">
              <Youtube className="inline mr-1" size={16} /> REPLAYS
            </a>
            <a href="/regras" className="flex-shrink-0 bg-zinc-100 hover:bg-zinc-200 px-4 py-2 rounded-lg font-bold text-sm transition-colors" data-testid="nav-rules">
              <FileText className="inline mr-1" size={16} /> REGRAS
            </a>
            <a href="/fantasy" className="flex-shrink-0 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white hover:from-emerald-600 hover:to-emerald-700 px-4 py-2 rounded-lg font-bold text-sm transition-colors" data-testid="nav-fantasy">
              <Trophy className="inline mr-1" size={16} /> RATINGPRO
            </a>
          </div>
        </div>
      </nav>

      <main className="max-w-6xl mx-auto px-4 py-6">
        <PWAInstallBanner />
        {/* WhatsApp Group Invite Banner (dismissible) */}
        {showWaBanner && (
          <section className="mb-6" data-testid="wa-group-banner">
            <div className="relative rounded-xl bg-gradient-to-r from-emerald-500 to-green-600 text-white shadow-lg border-2 border-emerald-700 overflow-hidden">
              <a
                href={WHATSAPP_GROUP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 p-4 pr-12 hover:opacity-95 transition-opacity"
                data-testid="wa-group-banner-link"
              >
                <div className="w-11 h-11 rounded-full bg-white/20 flex items-center justify-center shrink-0">
                  <MessageCircle size={22} strokeWidth={2.5} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] font-black uppercase tracking-widest opacity-90">📱 Grupo WhatsApp</p>
                  <p className="text-base sm:text-lg font-black leading-tight">Entra no grupo oficial do Domingão!</p>
                  <p className="text-xs opacity-90 hidden sm:block">Recebe primeiro: sorteios, escalação, replays e novidades.</p>
                </div>
                <span className="hidden sm:inline-block bg-white text-green-700 font-black px-4 py-2 rounded-lg text-sm shrink-0">
                  ENTRAR
                </span>
              </a>
              <button
                onClick={dismissWaBanner}
                aria-label="Fechar convite"
                className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/25 hover:bg-black/40 text-white flex items-center justify-center transition-colors"
                data-testid="wa-banner-dismiss"
              >
                <X size={14} strokeWidth={3} />
              </button>
            </div>
          </section>
        )}

        {/* Highlights Carousel */}
        {highlights.length > 0 && (
          <section className="mb-8" data-testid="highlights-section">
            <h2 className="text-xl font-black tracking-tight uppercase mb-4">
              🌟 DESTAQUES
            </h2>
            <HighlightCarousel highlights={highlights} collectivePhotoUrl={teamPhotoUrl} />
          </section>
        )}

        {/* Next Match Card */}
        {nextMatch && (
          <section className="mb-8" data-testid="next-match-section">
            <div className="bg-zinc-950 text-white rounded-2xl p-6">
              <div className="flex items-center gap-2 mb-3">
                <Calendar size={20} className="text-green-400" />
                <span className="text-xs font-bold uppercase tracking-wider text-green-400">Próxima Partida</span>
              </div>
              <h3 className="text-2xl font-black mb-2">{nextMatch.location}</h3>
              <p className="text-lg">
                📅 {new Date(nextMatch.date).toLocaleDateString('pt-PT', { weekday: 'long', day: 'numeric', month: 'long' })}
              </p>
              <p className="text-lg mb-4">⏰ {nextMatch.time}</p>
              <div className="flex gap-3">
                <span className="bg-zinc-800 px-3 py-1 rounded-full text-sm">
                  {nextMatch.confirmed_players?.length || 0}/{nextMatch.total_spots} vagas
                </span>
                <a 
                  href="/partidas" 
                  className="bg-green-500 hover:bg-green-600 px-4 py-1 rounded-full text-sm font-bold transition-colors"
                  data-testid="go-to-next-match"
                >
                  Ver Detalhes
                </a>
              </div>
            </div>
          </section>
        )}

        {/* Voting Status with CTA */}
        <section className="mb-8">
          <div className={`rounded-xl p-5 border-2 ${votingStatus.is_open ? 'bg-green-50 border-green-500' : 'bg-zinc-100 border-zinc-300'}`} data-testid="voting-status-banner">
            <p className="text-center font-bold text-lg mb-3">
              {votingStatus.is_open ? '🗳️ ' : '🔒 '}{votingStatus.message}
            </p>
            {votingStatus.is_open && (
              <div className="flex justify-center">
                <a
                  href="/votar"
                  className="bg-green-500 hover:bg-green-600 text-white font-black uppercase tracking-wider px-8 py-4 rounded-xl text-lg sm:text-xl shadow-lg transition-all hover:scale-105"
                  data-testid="cta-votar-agora"
                >
                  🗳️ VOTAR AGORA →
                </a>
              </div>
            )}
          </div>
        </section>

        {/* Leaderboards */}
        <section className="mb-8">
          <h2 className="text-xl font-black tracking-tight uppercase mb-4">🏆 LÍDERES</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Top Ranking */}
            <div className="bg-white rounded-xl border-2 border-zinc-200 p-4" data-testid="leaderboard-ranking">
              <h3 className="text-lg font-bold mb-3 pb-2 border-b border-zinc-200">
                🏅 Top Pontos_Rating
              </h3>
              <div className="space-y-2">
                {leaderboards.top_ranking.slice(0, 5).map((player, index) => (
                  <div key={index} className="flex justify-between items-center text-sm">
                    <span className="font-bold">{index + 1}. {player.name}</span>
                    <span className="font-bold text-green-600">{player.value.toFixed(2)} ⭐</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Top Scorers */}
            <div className="bg-white rounded-xl border-2 border-zinc-200 p-4" data-testid="leaderboard-scorers">
              <h3 className="text-lg font-bold mb-3 pb-2 border-b border-zinc-200">
                ⚽ Artilheiros
              </h3>
              <div className="space-y-2">
                {leaderboards.top_scorers.map((player, index) => (
                  <div key={index} className="flex justify-between items-center text-sm">
                    <span className="font-bold">{index + 1}. {player.name}</span>
                    <span className="font-bold text-green-600">{player.value} gols</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Top Assists */}
            <div className="bg-white rounded-xl border-2 border-zinc-200 p-4" data-testid="leaderboard-assists">
              <h3 className="text-lg font-bold mb-3 pb-2 border-b border-zinc-200">
                🎯 Assistências
              </h3>
              <div className="space-y-2">
                {leaderboards.top_assists.map((player, index) => (
                  <div key={index} className="flex justify-between items-center text-sm">
                    <span className="font-bold">{index + 1}. {player.name}</span>
                    <span className="font-bold text-green-600">{player.value} assist.</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Quick Actions */}
        <section className="mb-8">
          <div className="grid grid-cols-2 gap-3">
            <a 
              href="/estatisticas" 
              className="bg-zinc-950 text-white rounded-xl p-4 text-center hover:bg-zinc-800 transition-colors"
              data-testid="quick-stats"
            >
              <BarChart3 className="mx-auto mb-2" size={28} />
              <span className="font-bold text-sm">ESTATÍSTICAS</span>
            </a>
            <a 
              href="/rankings" 
              className="bg-green-500 text-white rounded-xl p-4 text-center hover:bg-green-600 transition-colors"
              data-testid="quick-rankings"
            >
              <Trophy className="mx-auto mb-2" size={28} />
              <span className="font-bold text-sm">RANKINGS</span>
            </a>
          </div>
        </section>
      </main>
    </div>
  );
};

export default HomePage;
